import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

interface TournamentConfig {
  autoAdvancement: boolean;
  allowLateRegistration: boolean;
  requirePayment: boolean;
  enableLiveStreaming: boolean;
  showBracketPublicly: boolean;
  sendNotifications: boolean;
  maxMatchDuration: number;
  breakBetweenRounds: number;
  defaultGameMode: '301' | '501' | 'cricket';
  bestOf: number;
}

const TournamentSettings: React.FC = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<TournamentConfig>({
    autoAdvancement: true,
    allowLateRegistration: false,
    requirePayment: false,
    enableLiveStreaming: true,
    showBracketPublicly: true,
    sendNotifications: true,
    maxMatchDuration: 30,
    breakBetweenRounds: 15,
    defaultGameMode: '501',
    bestOf: 3
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'system_settings', 'tournament_config');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setConfig(docSnap.data() as TournamentConfig);
      }
    } catch (error) {
      console.error('Error loading tournament settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await setDoc(doc(db, 'system_settings', 'tournament_config'), {
        ...config,
        updatedAt: Timestamp.now()
      });
      alert(t('tournament.settingsSaved'));
    } catch (error) {
      console.error('Error saving tournament settings:', error);
      alert(t('tournament.settingsSaveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card-dark p-6">
        <h3 className="text-2xl font-bold text-white mb-4">{t('tournament.generalSettings')}</h3>
        
        <div className="space-y-4">
          <div className="p-4 bg-[#15191E] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="text-lg font-semibold text-white">{t('tournament.autoAdvancement')}</h4>
                <p className="text-sm text-[#B8B8C3]">{t('tournament.autoAdvancementDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.autoAdvancement}
                  onChange={(e) => setConfig({ ...config, autoAdvancement: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
              </label>
            </div>
          </div>

          <div className="p-4 bg-[#15191E] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="text-lg font-semibold text-white">{t('tournament.allowLateRegistration')}</h4>
                <p className="text-sm text-[#B8B8C3]">{t('tournament.allowLateRegistrationDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.allowLateRegistration}
                  onChange={(e) => setConfig({ ...config, allowLateRegistration: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
              </label>
            </div>
          </div>

          <div className="p-4 bg-[#15191E] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="text-lg font-semibold text-white">{t('tournament.requirePayment')}</h4>
                <p className="text-sm text-[#B8B8C3]">{t('tournament.requirePaymentDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.requirePayment}
                  onChange={(e) => setConfig({ ...config, requirePayment: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
              </label>
            </div>
          </div>

          <div className="p-4 bg-[#15191E] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="text-lg font-semibold text-white">{t('tournament.enableLiveStreaming')}</h4>
                <p className="text-sm text-[#B8B8C3]">{t('tournament.enableLiveStreamingDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enableLiveStreaming}
                  onChange={(e) => setConfig({ ...config, enableLiveStreaming: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
              </label>
            </div>
          </div>

          <div className="p-4 bg-[#15191E] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="text-lg font-semibold text-white">{t('tournament.showBracketPublicly')}</h4>
                <p className="text-sm text-[#B8B8C3]">{t('tournament.showBracketPubliclyDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.showBracketPublicly}
                  onChange={(e) => setConfig({ ...config, showBracketPublicly: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
              </label>
            </div>
          </div>

          <div className="p-4 bg-[#15191E] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="text-lg font-semibold text-white">{t('tournament.sendNotifications')}</h4>
                <p className="text-sm text-[#B8B8C3]">{t('tournament.sendNotificationsDesc')}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.sendNotifications}
                  onChange={(e) => setConfig({ ...config, sendNotifications: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="card-dark p-6">
        <h3 className="text-2xl font-bold text-white mb-4">{t('tournament.matchSettings')}</h3>
        
        <div className="space-y-4">
          <div className="p-4 bg-[#15191E] rounded-lg">
            <h4 className="text-lg font-semibold text-white mb-2">{t('tournament.maxMatchDuration')}</h4>
            <p className="text-sm text-[#B8B8C3] mb-3">{t('tournament.maxMatchDurationDesc')}</p>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="15"
                max="120"
                step="5"
                value={config.maxMatchDuration}
                onChange={(e) => setConfig({ ...config, maxMatchDuration: Number(e.target.value) })}
                className="flex-1"
              />
              <span className="text-cyan-400 font-bold w-20 text-right">
                {config.maxMatchDuration} {t('tournament.minutes')}
              </span>
            </div>
          </div>

          <div className="p-4 bg-[#15191E] rounded-lg">
            <h4 className="text-lg font-semibold text-white mb-2">{t('tournament.breakBetweenRounds')}</h4>
            <p className="text-sm text-[#B8B8C3] mb-3">{t('tournament.breakBetweenRoundsDesc')}</p>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="5"
                max="60"
                step="5"
                value={config.breakBetweenRounds}
                onChange={(e) => setConfig({ ...config, breakBetweenRounds: Number(e.target.value) })}
                className="flex-1"
              />
              <span className="text-cyan-400 font-bold w-20 text-right">
                {config.breakBetweenRounds} {t('tournament.minutes')}
              </span>
            </div>
          </div>

          <div className="p-4 bg-[#15191E] rounded-lg">
            <h4 className="text-lg font-semibold text-white mb-2">{t('tournament.defaultGameMode')}</h4>
            <p className="text-sm text-[#B8B8C3] mb-3">{t('tournament.defaultGameModeDesc')}</p>
            <select
              value={config.defaultGameMode}
              onChange={(e) => setConfig({ ...config, defaultGameMode: e.target.value as TournamentConfig['defaultGameMode'] })}
              className="input-dark w-full"
            >
              <option value="301">301</option>
              <option value="501">501</option>
              <option value="cricket">Cricket</option>
            </select>
          </div>

          <div className="p-4 bg-[#15191E] rounded-lg">
            <h4 className="text-lg font-semibold text-white mb-2">{t('tournament.bestOf')}</h4>
            <p className="text-sm text-[#B8B8C3] mb-3">{t('tournament.bestOfDesc')}</p>
            <select
              value={config.bestOf}
              onChange={(e) => setConfig({ ...config, bestOf: Number(e.target.value) })}
              className="input-dark w-full"
            >
              <option value="1">Best of 1</option>
              <option value="3">Best of 3</option>
              <option value="5">Best of 5</option>
              <option value="7">Best of 7</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </div>
  );
};

export default TournamentSettings;
